import React, {useCallback} from "react"
import {observer} from "mobx-react"
import {isString} from "lodash"
import classes from "./ScreenHeader.module.css"
import cx from "classnames"
import {BackButton} from "components/buttons/BackButton"
import {Search, SearchProps} from "components/inputs/Search"
import {Notification} from "components/Notification"
import {Dialpad} from "components/Dialpad"
import {useModel} from "hooks/useModel"
import {Settings} from "types/settings"

export type ScreenHeaderProps = {
    className?: string
    title?: string | React.ReactNode
    htmlSubtitle?: string | React.ReactNode
    code?: string
    backUrl?: string
    search?: SearchProps
    isShowNotification?: boolean
    action?: JSX.Element
    htmlTitle?: any
    variant?: "row" | "column"
    backData?: any
    rightPanelDetected?: boolean
    titleClassName?: any
    customBackButton?: () => JSX.Element
    isShowDialpad?: boolean
    departmentId?: number
}

export const ScreenHeader = observer(
    ({
        className,
        title,
        code,
        backUrl,
        search,
        action,
        htmlSubtitle,
        htmlTitle,
        variant = "row",
        isShowNotification = true,
        titleClassName,
        backData,
        rightPanelDetected,
        customBackButton,
        isShowDialpad = true,
        departmentId
    }: ScreenHeaderProps) => {
        const model = useModel()

        const renderTitle = () => {
            if (isString(title)) {
                return (
                    <span className={cx(classes.title, {[classes.titleAlone]: !headerBackButton}, titleClassName)}>
                        {title}
                    </span>
                )
            }
            return title
        }

        const renderNotification = () => {
            return (
                <div
                    className={cx(classes.notificationWrap, {
                        [classes.notificationWrap__hasDrawer]: rightPanelDetected
                    })}>
                    <Notification />
                </div>
            )
        }

        const renderDialpad = useCallback(() => {
            const isVoiceCallEnabled = model.clientSetting.dialerProvider === Settings.DialerProvider.Twilio
            if (!isVoiceCallEnabled || !model.isStaffOrAdmin()) {
                return null
            }

            let fromPhoneNumber = model.smsPhoneNumbers.length > 0 ? model.smsPhoneNumbers[0] : null
            if (!fromPhoneNumber && departmentId) {
                fromPhoneNumber = model.smsAllPhoneNumbers.find((phone) => phone.departmentIdForDialer === departmentId)
            }
            if (!fromPhoneNumber) {
                return null
            }

            return (
                <div
                    className={cx(classes.notificationWrap, {
                        [classes.notificationWrap__hasDrawer]: rightPanelDetected
                    })}>
                    <Dialpad departmentId={departmentId} />
                </div>
            )
        }, [model, departmentId, rightPanelDetected])

        const headerBackButton = customBackButton
            ? customBackButton()
            : backUrl && <BackButton url={backUrl} className={classes.backButton} backData={backData} />

        return (
            <div className={cx(classes.root, className)}>
                {variant === "row" ? (
                    <>
                        {headerBackButton}
                        {renderTitle()}
                        {!!code && <span className={classes.code}>({code})</span>}
                        {search && <Search {...search} className={cx(classes.search, search.className)} />}
                        {action}
                        <div className={classes.rightWrap}>
                            {isShowDialpad ? renderDialpad() : null}
                            {isShowNotification ? renderNotification() : null}
                        </div>
                    </>
                ) : (
                    <div className={classes.headerWrap}>
                        <div className={classes.headerLeftWrap}>
                            <BackButton url={backUrl} className={classes.backButton} backData={backData} />
                        </div>
                        <div className={cx(classes.headerRightWrap, !title && !htmlSubtitle ? classes.flexCenter : "")}>
                            <p className={classes.headerRightTitle}>{htmlTitle}</p>
                            {title && (
                                <div className={classes.headerRightInfo}>
                                    <>
                                        <p className={classes.headerRightInfo__title}>{title}</p>
                                        <p className={classes.headerRightInfo__code}>{code}</p>
                                    </>
                                </div>
                            )}
                            {htmlSubtitle && (
                                <p style={{marginTop: 5}} className={classes.headerRightInfo__code}>
                                    {htmlSubtitle}
                                </p>
                            )}
                        </div>
                        <div className={classes.rightWrap}>
                            {isShowDialpad ? renderDialpad() : null}
                            {isShowNotification ? renderNotification() : null}
                        </div>
                    </div>
                )}
            </div>
        )
    }
)
